import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from 'react-toastify';
import { api } from '../../../app/Api/ApiSlice';
import "../storesStyles.css";
import { FaTimes } from 'react-icons/fa';

const EditProduct = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { name } = useParams();
    const { product } = location.state;

    const [productName, setProductName] = useState(product?.title || "");
    const [description, setDescription] = useState(product?.description || "");
    const [price, setPrice] = useState(product?.price || "");
    const [stock, setStock] = useState(product?.stock || "");
    const [selectedShelves, setSelectedShelves] = useState(product?.shelves || []);
    const [files, setFiles] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);
    const [shelves, setShelves] = useState([]);
    const [openCreateShelf, setOpenCreateShelf] = useState(false);
    const [shelfName, setShelfName] = useState("");
    const [shelfDescription, setShelfDescription] = useState("");
    const [creating, setCreating] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Load shelves
    useEffect(() => {
        const getShelves = async () => {
            try {
                const response = await api.get(`/stores/${name}/manage/product/shelves`);
                if (response?.data) {
                    setShelves(response.data);
                }
            } catch (error) {
                console.error("Error fetching shelves", error);
            }
        };
        getShelves();
    }, [name]);

    // Handle new files
    const addFiles = (e) => {
        const selectedFiles = Array.from(e.target.files);
        setFiles(selectedFiles);
        const previews = selectedFiles.map(file => URL.createObjectURL(file));
        setImagePreviews(previews);
    };

    useEffect(() => {
        return () => {
            imagePreviews.forEach(url => URL.revokeObjectURL(url));
        };
    }, [imagePreviews]);

    const handleRemove = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!productName || !price) {
            toast.warn("Please fill in all required fields");
            return;
        }

        const formData = new FormData();
        formData.append("title", productName);
        formData.append("description", description);
        formData.append("price", price);
        formData.append("stock", stock);
        formData.append("shelves", JSON.stringify(selectedShelves));
        files.forEach(file => formData.append("file", file));

        try {
            setIsLoading(true);
            await api.put(`/stores/${name}/manage/products/${product._id}`, formData);
            toast.success("Product updated successfully!");
            navigate(`/b/manage/${name}/products`);
        } catch (error) {
            toast.error(error.response?.data?.message || "Error updating product");
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateShelf = async (e) => {
        e.preventDefault();
        const newShelf = { name: shelfName, description: shelfDescription };

        try {
            setCreating(true);
            const response = await api.post(`stores/${name}/manage/shelves`, { newShelf });
            if (response) {
                setShelves(prev => [...prev, response.data]);
                toast.success("Shelf created");
                setShelfName("");
                setShelfDescription("");
                setOpenCreateShelf(false);
            }
        } catch (error) {
            toast.error(error.response?.data?.message || "Error creating shelf");
        } finally {
            setCreating(false);
        }
    };

    return (
        <div>
            <div className={openCreateShelf ? "open-create-shelf-modal" : "close-create-shelf-modal"}>
                <form onSubmit={handleCreateShelf}>
                    <div className='create-shelf-btn-wrapper'>
                        <button onClick={() => setOpenCreateShelf(false)} className="close-create-shelf">
                            <FaTimes />
                        </button>
                    </div>
                    <h3 className="modal-title">Create New Shelf</h3>
                    <label>Name</label>
                    <input type="text" required placeholder='Shelf name' onChange={(e) => setShelfName(e.target.value)} />
                    <label>Description</label>
                    <input type="text" required placeholder='Shelf description' onChange={(e) => setShelfDescription(e.target.value)} />
                    <button>{creating ? "Loading..." : "Save"}</button>
                </form>
            </div>

            <div className="form-wrapper">
                <h2 className="event-form-title">Edit Product</h2>
                <form className="project-form" onSubmit={handleSubmit}>
                    <label>Product Name</label>
                    <input type="text" value={productName} onChange={(e) => setProductName(e.target.value)} />

                    <label>Description</label>
                    <input type="text" className="event-description" value={description} onChange={(e) => setDescription(e.target.value)} />

                    <label>Price</label>
                    <input type="number" value={price} onChange={(e) => setPrice(e.target.value)} />

                    <label>Shelves</label>
                    <p onClick={() => setOpenCreateShelf(true)} className="clickable-text">Create a shelf</p>
                    <select
                        multiple
                        value={selectedShelves}
                        onChange={(e) => setSelectedShelves(Array.from(e.target.selectedOptions, o => o.value))}
                        className="shelves-add-select"
                    >
                        {shelves.map((shelf) => (
                            <option key={shelf._id} value={shelf._id}>
                                {shelf.name}
                            </option>
                        ))}
                    </select>

                    <label>Stock</label>
                    <input type="number" value={stock} onChange={(e) => setStock(e.target.value)} />

                    <label>Upload New Images (Optional)</label>
                    <input type="file" onChange={addFiles} multiple />

                    {imagePreviews.length > 0 && (
                        <div className="images-preview">
                            {imagePreviews.map((preview, i) => (
                                <div key={i} className="form-image">
                                    <img src={preview} alt={`Preview ${i}`} className="image-preview" />
                                    <div className="remove-img">
                                        <button type="button" onClick={() => handleRemove(i)}>Remove</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="post-form-btn">
                        <button type="submit" disabled={isLoading}>
                            {isLoading ? "Updating..." : "Update"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditProduct;
